import React, { useEffect } from "react";
import { observer, useLocalStore } from "mobx-react";
import { useParams } from "react-router-dom";
import { ws } from "@app/lib/socket";

import {
    UndoOutlined,
    DeleteOutlined,
    CloudDownloadOutlined,
    EllipsisOutlined,
    EditOutlined,
    MessageOutlined,
    DeploymentUnitOutlined,
    FileDoneOutlined,
    RestOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import { Button, Menu, Dropdown, Modal } from "antd";

import UploadMenuItem from "@app/components/form/upload/menu-item";
import confirm from "@app/components/confirm";
import file from "@app/lib/file";
import { Review, SearchType } from "@app/constants";
import EditModal from "./edit/article";
import Note from "./note";
import format from "@app/lib/format";
import ManageFilesModal from "../manage-files-modal";
import ArticleGalaxyMenuItem from "@app/components/article-galaxy/menu-item";
import store from "@app/state/store/report/article";

import articleGalaxyUtils from "../utils/article-galaxy";

import "./style/file-menu.scoped.scss";

const ArticleMenu = observer(
    ({
        article = {},
        onUpload,
        onRemove,
        onChange,
        switchArticleType = false,
        onChangeType,
        onResetStatus,
        stage,
        onRemoveArticle,
        fileId,
        readonly = false,
    }) => {
        const { type } = useParams();
        const l1Excluded = article?.review?.l1?.status === Review.FAIL;
        const l2Excluded = article?.review?.l2?.status === Review.FAIL;

        const crossChannelArticle = article.crossChannel.used;
        const canSwitchType =
            (l1Excluded || l2Excluded) && !crossChannelArticle && switchArticleType;
        const hasNote = !!article.note;
        const enableResetStatus = !!article.review?.[stage]?.status;
        const enableArticleDelete = article.moved && article.originalSearch;
        const hasSupplementaryFiles = article.supplementaryFiles?.length > 0;

        useEffect(() => {
            const handler = () => {
                Modal.warning({
                    title: "Export Errors",
                    content: `There was an erorr while adding the highlights to the full text.
                                The downloaded file will not include the hightlights.`,
                    okText: "OK",
                    onOk(close) {
                        close();
                    },
                });
            };

            ws.on("export.errors", handler);
            return () => ws.off("export.errors", handler);
        }, []);

        var state = useLocalStore(() => ({
            editArticle: false,
            addNote: false,
            manageFilesVisible: false,
        }));

        const remove = async () => {
            let review = article.review?.l2 || {};
            let started = review.started || review.complete;

            const message = started
                ? "The L2 review process for this article has already started. Are you sure that you want to remove the full text for this article?"
                : "Are you sure that you want to remove the full text for this article?";

            const proceed = await confirm(message);
            if (proceed) {
                onRemove();
            }
        };

        const handleArticleGalaxyAction = async () => {
            articleGalaxyUtils.order(article);
        };

        const selectedFile = () => {
            let file = fileId;
            let index = 0;
            let name = article.title;

            if (stage === "l1" && article.file) {
                file = article.file;
                index = 0;
                name = article.title;
            } else {
                if (file === article.file) {
                    index = 0;
                    name = article.title;
                } else {
                    index = article.supplementaryFiles.findIndex((f) => f._id === file) || 0;
                    name = article.supplementaryFiles[index]?.name || article.title;
                    index += 1;
                }
            }

            const fileName = format.getFileName(
                article,
                {
                    _id: file,
                    name,
                    isMain: index === 0,
                },
                index,
            );

            return {
                _id: file,
                name,
                fileName,
            };
        };

        const onAction = (action) => {
            if (action.key === "remove") {
                remove();
            } else if (action.key === "download") {
                const selected = selectedFile();
                file.download(selected._id, selected.fileName);
            } else if (action.key === "download-with-highlights") {
                const selected = selectedFile();
                file.downloadWithHighlights(selected._id, selected.fileName, article._id);
            } else if (action.key === "editArticle") {
                state.editArticle = true;
            } else if (action.key === "addNote") {
                state.addNote = true;
            } else if (action.key === "addToSota") {
                onChangeType && onChangeType(SearchType.SOTA);
            } else if (action.key === "addToSLR") {
                onChangeType && onChangeType(SearchType.SLR);
            } else if (action.key === "resetStatus") {
                onResetStatus && onResetStatus();
            } else if (action.key === "deleteArticle") {
                onRemoveArticle && onRemoveArticle();
            }
        };

        const handleAddSupplementaryFile = async (file) => {
            await store.addSupplementaryFile(article._id, file._id);
        };

        if (readonly && !article.file) {
            return null;
        }

        /**
         * Prepare the actions menu
         */
        const actions = (
            <Menu onClick={onAction} className="menu">
                <Menu.ItemGroup title="Full Text">
                    {!readonly && !article.file && (
                        <Menu.Item key="upload">
                            <UploadMenuItem
                                accept=".pdf,application/pdf"
                                onChange={(data) => {
                                    onUpload(data);
                                }}
                            >
                                Upload
                            </UploadMenuItem>
                        </Menu.Item>
                    )}

                    {!readonly && article.file && stage === "l2" && (
                        <Menu.Item key="upload-supplementary">
                            <UploadMenuItem
                                accept=".pdf,application/pdf"
                                onChange={handleAddSupplementaryFile}
                            >
                                Upload Supplementary File
                            </UploadMenuItem>
                        </Menu.Item>
                    )}

                    {article.file && (
                        <Menu.Item key="download" icon={<CloudDownloadOutlined />}>
                            Download
                        </Menu.Item>
                    )}

                    {article.file && (
                        <Menu.Item key="download-with-highlights" icon={<CloudDownloadOutlined />}>
                            Download + highlights
                        </Menu.Item>
                    )}

                    {!readonly && article.file && hasSupplementaryFiles && (
                        <Menu.Item
                            key="manage-files"
                            icon={<SettingOutlined />}
                            onClick={() => (state.manageFilesVisible = true)}
                        >
                            Manage Uploaded Files
                        </Menu.Item>
                    )}

                    {!readonly && article.file && (
                        <Menu.Item key="remove" icon={<DeleteOutlined />}>
                            Remove
                        </Menu.Item>
                    )}
                </Menu.ItemGroup>

                {!readonly && (
                    <Menu.ItemGroup title="Action">
                        <Menu.Item key="editArticle" icon={<EditOutlined />}>
                            Edit Details
                        </Menu.Item>

                        {!hasNote && (
                            <Menu.Item key="addNote" icon={<MessageOutlined />}>
                                Add a Note
                            </Menu.Item>
                        )}

                        {canSwitchType && type === SearchType.SLR && (
                            <Menu.Item key="addToSota" icon={<DeploymentUnitOutlined />}>
                                Use in SoTA
                            </Menu.Item>
                        )}

                        {canSwitchType && type === SearchType.SOTA && (
                            <Menu.Item key="addToSLR" icon={<FileDoneOutlined />}>
                                Use in DUE
                            </Menu.Item>
                        )}

                        {enableResetStatus && (
                            <Menu.Item key="resetStatus" icon={<UndoOutlined />}>
                                Reset Status
                            </Menu.Item>
                        )}

                        {enableArticleDelete && (
                            <Menu.Item key="deleteArticle" icon={<RestOutlined />}>
                                Remove Article
                            </Menu.Item>
                        )}
                    </Menu.ItemGroup>
                )}

                {!readonly && articleGalaxyUtils.isOrderEnabled() && (
                    <Menu.ItemGroup title="Purchase">
                        <Menu.Item
                            key="article-galaxy"
                            onClick={handleArticleGalaxyAction}
                            disabled={!articleGalaxyUtils.canOrder(article)}
                        >
                            <ArticleGalaxyMenuItem article={article} />
                        </Menu.Item>
                    </Menu.ItemGroup>
                )}
            </Menu>
        );

        return (
            <>
                <Dropdown overlay={actions} trigger="click" placement="bottomRight">
                    <Button className="drop-down-button" icon={<EllipsisOutlined />}></Button>
                </Dropdown>
                {state.editArticle && (
                    <EditModal
                        article={article}
                        onChange={(data) => {
                            onChange(data);
                            state.editArticle = false;
                        }}
                        onCancel={() => {
                            state.editArticle = false;
                        }}
                    />
                )}

                {state.addNote && (
                    <Note
                        className="group"
                        text={article.note}
                        add={state.addNote}
                        onChange={(note) => {
                            onChange({ note });
                            state.addNote = false;
                        }}
                        onCancel={() => {
                            state.addNote = false;
                        }}
                    />
                )}

                {state.manageFilesVisible && (
                    <ManageFilesModal
                        article={article}
                        visible={state.manageFilesVisible}
                        onCancel={() => (state.manageFilesVisible = false)}
                    />
                )}
            </>
        );
    },
);

export default ArticleMenu;
